<template>
    <div class="w-full h-full max-h-[calc(100%-2rem)] space-y-2  ">
        <div class="w-full h-full  flex space-x-2 ">
            <div class="w-1/2 bg-white/10 rounded-lg p-2 ">

                <!-- Tabs -->
                <div class="flex items-center text-sm font-bold justify-between border-b border-white/20 mb-2 pb-1">
                    <div class="flex items-center space-x-4">
                        <div class="flex">
                            <button class="pl-2 pr-1"
                                :class="activeTab === 'design' ? 'border-b-2 border-ui-active text-ui-active' : 'text-slate-400'"
                                @click="activeTab = 'design'">
                                Design Resources
                            </button>

                            <div class="rounded-full w-4 h-4 text-center text-xs text-white mt-1"
                                :class="activeTab === 'design' ? ' bg-ui-active ' : 'bg-white/20'">
                                {{ designResources.length }}
                            </div>
                        </div>
                        <div class="flex">
                            <button class="px-2 "
                                :class="activeTab === 'all' ? 'border-b-2 border-ui-active text-ui-active' : 'text-slate-400'"
                                @click="activeTab = 'all'">
                                All Resources
                            </button>
                            <div class="rounded-full w-4 h-4 text-center text-xs text-white mt-1"
                                :class="activeTab === 'all' ? ' bg-ui-active ' : 'bg-white/20'">
                                {{ allResources.length }}
                            </div>
                        </div>
                    </div>
                    <form-button class="text-xs px-2 py-1" :disabled="isBulkUploading || designResources.length === 0"
                        @click="uploadAllUsedResources">
                        <template v-if="isBulkUploading">Uploading {{ bulkUploadProgress.done }}/{{ bulkUploadProgress.total }}...</template>
                        <template v-else>Upload All Used</template>
                    </form-button>
                </div>

                <!-- Tab Content -->
                <div v-if="activeTab === 'design'" class="overflow-auto  max-h-[calc(100%-2rem)]  no-scrollbar">
                    <ul class="space-y-2">
                        <li v-for="(resource, index) in designResources" :key="'design-' + index"
                            class="bg-white/10 p-2 rounded shadow cursor-pointer text-slate-200 text-sm" draggable="true"
                            @dragstart="onDragStart(resource, 'local')">
                            <img v-if="resource.resourceType === 'image'" :src="resource.src" alt="Image" class="h-16 inline-block mr-2" />
                            <span v-else-if="resource.resourceType === 'audio'" class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/10 text-slate-200 text-2xl">
                                <font-awesome-icon icon="fa-solid fa-volume-high" /></span>
                            <span v-else-if="resource.resourceType === 'synth'" class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/10 text-cyan-200 text-2xl">♪</span>
                            <span v-else-if="resource.resourceType === 'model3d'" class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/10 text-slate-200 text-2xl">
                                <font-awesome-icon icon="fa-solid fa-cube" /></span>
                            <span v-else class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/10 text-slate-200 text-2xl overflow-hidden"
                                :style="fontPreviewStyle(resource)">Aa</span>
                            <span>{{ resource.filename }}</span>
                        </li>
                    </ul>
                </div>

                <div v-if="activeTab === 'all'" class="overflow-auto  max-h-[calc(100%-2rem)]  no-scrollbar">
                    <ul class="space-y-2">
                        <li v-for="(resource, index) in allResources" :key="'all-' + index"
                            class="bg-white/15 p-2 rounded shadow cursor-pointer text-slate-200 text-sm" draggable="true"
                            @dragstart="onDragStart(resource, 'local')">
                            <img v-if="resource.resourceType === 'image'" :src="resource.src" alt="Image"
                                class="bg-white/15 aspect-square rounded-lg shadow-md h-16 inline-block mr-2" />
                            <span v-else-if="resource.resourceType === 'audio'" class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/15 text-slate-200 text-2xl">
                                <font-awesome-icon icon="fa-solid fa-volume-high" /></span>
                            <span v-else-if="resource.resourceType === 'synth'" class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/15 text-cyan-200 text-2xl">♪</span>
                            <span v-else-if="resource.resourceType === 'model3d'" class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/15 text-slate-200 text-2xl">
                                <font-awesome-icon icon="fa-solid fa-cube" /></span>
                            <span v-else class="inline-flex items-center justify-center h-16 w-16 mr-2 rounded bg-white/15 text-slate-200 text-2xl overflow-hidden"
                                :style="fontPreviewStyle(resource)">Aa</span>
                            <span>{{ resource.filename }}</span>
                        </li>
                    </ul>
                </div>
            </div>

            <!-- Remote Files Pane -->
            <div class="relative w-1/2 h-full overflow-auto bg-white/10 rounded-lg p-2 no-scrollbar"
                :class="{ 'bg-white/20': isDraggingRemote }" @dragover.prevent @dragenter="isDraggingRemote = true"
                @dragleave="isDraggingRemote = false" @drop="onDrop('remote'); isDraggingRemote = false">
                <h3 class="text-sm text-ui-active font-semibold mb-2 px-4">Remote Files {{ sortedRemoteFiles.length }}</h3>
                <div v-if="isLoadingRemote"
                    class="absolute inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center">
                    <div class="w-8 h-8 border-4 border-ui-active border-t-transparent rounded-full animate-spin"></div>
                </div>
                <ul class="space-y-1">
                    <li v-for="(file, index) in sortedRemoteFiles" :key="'remote-' + index" :class="{
                        'bg-white/15 p-2 rounded shadow cursor-pointer': isSyncableFile(file.filename),
                        'bg-white/10 p-2 rounded cursor-not-allowed': !isSyncableFile(file.filename),
                    }" :draggable="isImageFile(file.filename)"
                        @dragstart="isImageFile(file.filename) ? onDragStart(file, 'remote') : null">
                        <div class="flex items-center relative">
                            <div class="w-full text-sm text-clip mr-2 text-slate-200">{{ file.filename }}</div>
                            <p class="w-20 min-w-16 text-xs text-slate-400">{{ formatFileSize(file.file_size) }}</p>
                            <p class="w-20 text-xs text-nowrap text-slate-400">{{ formatDate(file.modified) }}</p>
                            <button v-if="isImageFile(file.filename)"
                                class="ml-2 p-1 rounded text-slate-400 hover:bg-white/10 hover:text-ui-active"
                                :class="{ 'bg-green-900/40 text-green-400': successfullySaved.has(file.filename) }"
                                title="Save to image store"
                                :disabled="savingToStore.has(file.filename)"
                                @click="downloadToImageStore(file.filename)">
                                <template v-if="savingToStore.has(file.filename)">
                                    <!-- Spinner SVG -->
                                    <svg class="animate-spin w-4 h-4 text-ui-active" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                    </svg>
                                </template>
                                <template v-else-if="successfullySaved.has(file.filename)">
                                    <!-- Checkmark SVG Icon -->
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M5 13l4 4L19 7" />
                                    </svg>
                                </template>
                                <template v-else>
                                    <!-- Image/Photo SVG Icon -->
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                </template>
                            </button>
                            <button v-if="isSyncableFile(file.filename)" class="ml-2 text-red-400 hover:text-red-300"
                                @click="toggleDeleteConfirmation(index)">
                                <font-awesome-icon icon="fa-solid fa-trash-can" />
                            </button>

                            <!-- Confirmation Balloon -->
                            <div v-if="deleteConfirmationIndex === index"
                                class="absolute top-0 right-0 bg-ui-bg border border-white/20 rounded shadow p-2 z-50">
                                <p class="text-sm text-white">Are you sure?</p>
                                <div class="flex space-x-2 mt-1 justify-between">
                                    <form-button color="danger" class="w-10" @click="confirmDelete(file.filename)">
                                        Yes
                                    </form-button>
                                    <form-button class="w-10" @click="cancelDelete">
                                        No
                                    </form-button>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
        <div class="w-full bg-white/10 border border-white/20 rounded-md p-1 text-white">
            <div class="flex w-full justify-between">
                <div class="ml-2">Connected to {{ settings.plate.name }}</div>
                <div class="mr-2 text-ui-active" v-html="uploadStatus" />
            </div>
            <div v-if="isBulkUploading" class="mx-2 mt-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div class="h-full bg-ui-active transition-all" :style="{ width: bulkUploadPercent + '%' }"></div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watchEffect, watch, computed } from "vue";
import { debounce, get } from "lodash";
import { findHaspObjectByType } from '../../utils/hasputils';
import { invoke } from '@tauri-apps/api/core';
import { useImageStore } from "../../stores/imageStore";
import { useFontStore } from "../../stores/fontStore";
import { useAudioStore } from "../../stores/audioStore";
import { useSynthStore } from "../../stores/synthStore";
import { useModel3dStore } from "../../stores/model3dStore";
import { getFontFile } from "../../services/resourceStorage.js";
import { getAudioFile } from "../../services/resourceStorage.js";
import { isResourceUsed, findWidgetsUsingActionResource } from "../../utils/resourceUsage";
import { DesignerFontLoader } from "../../services/DesignerFontLoader";
import { deploySynthReferenceToPlate, synthDeviceFilename } from "../../services/synthDeploy";
import { buildUsedH3dResourceArtifacts, h3dRuntimeFilename } from "../../services/h3dResourcePipeline";
import {
    dataUrlToBase64,
    deleteDeviceFile,
    downloadDeviceFile,
    listDeviceFiles,
    uploadDeviceFile,
} from "../../services/httpTransfer";

const imageStore = useImageStore();
const fontStore = useFontStore();
const audioStore = useAudioStore();
const synthStore = useSynthStore();
const model3dStore = useModel3dStore();

const props = defineProps({
    popupState: Boolean, // Detect when tab opens
    settings: {
        type: Object,
        required: true,
    },
    layer: {
        type: Object,
        required: true,
        default: () => ({}),
    },
    layerUpdate: {
        type: Number,
        required: true,
    },
    imageStoreUpdated: {
        type: Number,
        required: true,
    }
});

const emit = defineEmits(['imageStoreUpdated']);

const uploadStatus = ref('ready');
const exportImages = ref([]);
const exportFonts = ref([]);
const exportAudio = ref([]);
const exportModels = ref([]);
const selectedImages = ref([]);
const fileListRef = ref([]);

const activeTab = ref('design'); // Tracks the active tab ('design' or 'all')
const allImages = ref([]); // Stores all images from the imageDB

const isBulkUploading = ref(false);
const bulkUploadProgress = ref({ done: 0, total: 0 });
const bulkUploadPercent = computed(() =>
    bulkUploadProgress.value.total ? Math.round((bulkUploadProgress.value.done / bulkUploadProgress.value.total) * 100) : 0
);

const isDragging = ref(false);
const isDraggingRemote = ref(false);

const draggedFile = ref(null);
const draggedSource = ref(null);

const isLoadingLocal = ref(false);
const isLoadingRemote = ref(false);

const deleteConfirmationIndex = ref(null); // Tracks which file's delete confirmation is active

// Track files being saved to image store
const savingToStore = ref(new Set());
// Track successfully saved files (for visual feedback)
const successfullySaved = ref(new Set());

// Chunked conversion avoids O(n²) string concatenation that freezes the UI for large files
function bytesToBase64(bytes) {
    const CHUNK = 65536;
    let binary = '';
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
}

const updateResourceList = debounce(() => {
    exportImages.value = findHaspObjectByType(props.layer, 'img');
    exportFonts.value = fontStore.all.filter((font) => font.file && isResourceUsed(props.layer, 'font', font.file));
    // Audio clips referenced by an `audio.play` action. The reference is a
    // typed action's per-event operand rather than a widget property, so this
    // uses the action walker instead of isResourceUsed().
    exportAudio.value = audioStore.all.filter((clip) => clip.type !== 'audio_url' && findWidgetsUsingActionResource(props.layer, clip.uuid).length > 0);
    exportModels.value = model3dStore.all.filter((model) => isResourceUsed(props.layer, 'resource', model.uuid));
}, 100); // Adjust the debounce delay as necessary

// Watch for layerUpdate changes
watch(() => props.layerUpdate, (newVal) => {
    if (newVal) {
        updateResourceList();
    }
});

watch(() => props.imageStoreUpdated, (newVal) => {
    if (newVal) {
        console.log('Image store updated, fetching all images...');
        fetchAllImages();
    }
});

const getRemoteFileList = async () => {
    isLoadingRemote.value = true;
    try {
        const fileList = await listDeviceFiles(props.settings.plate);
        fileListRef.value = fileList
    } catch (error) {
        console.error('Failed to list device files:', error);
        uploadStatus.value = 'Failed to list device files: ' + error;
        return;
    }
    finally {
        isLoadingRemote.value = false; // Stop spinner for remote
    }
};

// Fetch all images from the imageDB
const fetchAllImages = async () => {
    await imageStore.load();
    allImages.value = imageStore.all;
};

watchEffect(async () => {
    if (props.popupState) {
        updateResourceList();
        fetchAllImages();
        getRemoteFileList();
    }
});

const getRemoteFiles = async () => {
    isLoadingRemote.value = true;
    try {
        const fileList = await listDeviceFiles(props.settings.plate);
        fileListRef.value = fileList
    } catch (error) {
        console.error('Failed to list device files:', error);
        uploadStatus.value = 'Failed to list device files: ' + error;
        return;
    }
    finally {
        isLoadingRemote.value = false; // Stop spinner for remote
    }
};

const sortedRemoteFiles = computed(() => {
    return [...fileListRef.value].sort((a, b) => {
        const aIsSyncable = isSyncableFile(a.filename);
        const bIsSyncable = isSyncableFile(b.filename);

        // Syncable (image/font) files should come first
        if (aIsSyncable && !bIsSyncable) return -1;
        if (!aIsSyncable && bIsSyncable) return 1;

        // If both are the same type, keep the original order
        return 0;
    });
});

const designExportImages = computed(() => {
    const seen = new Set();
    return exportImages.value.filter((file) => {
        if (seen.has(file.filename)) {
            return false; // Skip duplicates
        }
        seen.add(file.filename);
        return true;
    });
});

// A font resource's export identity is its on-device `file` path (e.g.
// "L:/7segment.ttf") — the remote filename is just that path's basename.
const fontDeviceFilename = (font) => font.file?.split(/[\\/]/).pop() || font.originalFilename || `${font.id}.ttf`;

const designFonts = computed(() =>
    exportFonts.value.map((font) => ({
        resourceType: 'font',
        filename: fontDeviceFilename(font),
        storageId: font.storageId,
        uuid: font.uuid,
        name: font.name,
    }))
);

const allFonts = computed(() =>
    fontStore.all.map((font) => ({
        resourceType: 'font',
        filename: fontDeviceFilename(font),
        storageId: font.storageId,
        uuid: font.uuid,
        name: font.name,
    }))
);

// An audio resource's export identity is its `filename` — the plate path is
// `L:/<filename>` (see audioStore.audioDevicePath), which is exactly what the
// exported `audio L:/<filename>` action refers to, so the remote filename is the
// filename verbatim.
const toAudioResource = (clip) => {
    if (clip.type === 'synth') {
        const synth = synthStore.getSynth(clip.synthId);
        return {
            resourceType: 'synth',
            filename: synthDeviceFilename(synth) || `[missing synth ${clip.synthId}]`,
            synthId: clip.synthId,
            uuid: clip.uuid,
            name: synth?.name || clip.name,
        };
    }
    return {
        resourceType: 'audio',
        filename: clip.filename,
        storageId: clip.storageId,
        uuid: clip.uuid,
        name: clip.name,
    };
};

const audioResourceDescriptors = (clips) => {
    const seenSynths = new Set();
    return clips.flatMap((clip) => {
        if (clip.type === 'audio_url') return [];
        if (clip.type === 'synth') {
            if (seenSynths.has(clip.synthId)) return [];
            seenSynths.add(clip.synthId);
        }
        return [toAudioResource(clip)];
    });
};

const designAudio = computed(() => audioResourceDescriptors(exportAudio.value));
const allAudio = computed(() => audioResourceDescriptors(audioStore.all));

const createModelResourceMapper = () => {
    const used = new Set();
    return (model) => {
        const filename = h3dRuntimeFilename(model, used);
        used.add(filename);
        return {
            resourceType: 'model3d',
            filename,
            sourceFilename: model.filename,
            storageId: model.storageId,
            uuid: model.uuid,
            name: model.name,
        };
    };
};

const designModels = computed(() => {
    const mapper = createModelResourceMapper();
    return exportModels.value.map(mapper);
});

const allModels = computed(() => {
    const mapper = createModelResourceMapper();
    return model3dStore.all.map(mapper);
});

// Unified "resources" lists drive the popup UI — images, fonts and audio are
// all synced to the plate the same way (drag-and-drop, or the bulk-upload
// button), they just differ in where their bytes come from.
const designResources = computed(() => [
    ...designExportImages.value.map((file) => ({ resourceType: 'image', filename: file.filename, src: file.src })),
    ...designFonts.value,
    ...designAudio.value,
    ...designModels.value,
]);

const allResources = computed(() => [
    ...allImages.value.map((file) => ({ resourceType: 'image', filename: file.filename, src: file.src })),
    ...allFonts.value,
    ...allAudio.value,
    ...allModels.value,
]);

// Same live preview as FontEditor.vue's swatch: load each font's uploaded
// bytes into a real FontFace so the list can render actual glyphs instead of
// a placeholder. allFonts is a superset of designFonts, so watching it alone
// covers both tabs.
watch(allFonts, (fonts) => {
    for (const font of fonts) {
        if (font.storageId != null) DesignerFontLoader.loadFont(font.uuid);
    }
}, { immediate: true });

const fontPreviewStyle = (resource) => {
    const family = DesignerFontLoader.getFontFamily(resource.uuid);
    return {
        fontFamily: family || 'inherit',
        fontStyle: family ? 'normal' : 'italic',
    };
};

const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif'];
const fontExtensions = ['.ttf', '.otf', '.woff', '.woff2'];
const audioExtensions = ['.wav'];
const model3dExtensions = ['.h3d0'];

const isImageFile = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    return imageExtensions.includes(`.${extension}`);
};

const isFontFile = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    return fontExtensions.includes(`.${extension}`);
};

const isAudioFile = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    return audioExtensions.includes(`.${extension}`);
};

const isSynthFile = (filename) => filename.toLowerCase().endsWith('.synth');

const isModel3dFile = (filename) => {
    const extension = filename.toLowerCase().split('.').pop();
    return model3dExtensions.includes(`.${extension}`);
};

const isSyncableFile = (filename) => isImageFile(filename) || isFontFile(filename) || isAudioFile(filename) || isSynthFile(filename) || isModel3dFile(filename);

const onDragStart = (file, source) => {
    console.log('Drag started:', file, source);
    draggedFile.value = file;
    draggedSource.value = source;
};

const onDragOver = (target, event) => {
    if (draggedSource.value === 'remote' && target === 'local') {
        // Check if the file already exists in the local files
        const isDuplicate = exportImages.value.some(
            (file) => file.filename === draggedFile.value.filename
        );

        if (isDuplicate) {
            event.dataTransfer.dropEffect = 'none'; // Show forbidden pointer
        } else {
            event.dataTransfer.dropEffect = 'copy'; // Allow drop
        }
    } else {
        event.dataTransfer.dropEffect = 'none'; // Default to forbidden pointer
    }
};

// The raw bytes to upload for a resource — already in-memory as a data URL
// for images, but a font's bytes live in FontDB and must be fetched by its
// storageId.
const getUploadPayload = async (resource, h3dArtifacts = null) => {
    if (resource.resourceType === 'model3d') {
        const artifacts = h3dArtifacts ?? await buildUsedH3dResourceArtifacts(props.layer, model3dStore);
        const artifact = artifacts
            .find((item) => item.uuid === resource.uuid);
        if (!artifact) throw new Error(`3D model "${resource.name || resource.filename}" is not used in this design.`);
        return artifact.base64data;
    }
    if (resource.resourceType === 'audio') {
        if (resource.storageId == null) throw new Error(`Audio "${resource.filename}" has no imported file yet.`);
        const record = await getAudioFile(resource.storageId);
        if (!record) throw new Error(`Audio file for "${resource.filename}" is missing from storage.`);
        return record.data;
    }
    if (resource.resourceType === 'font') {
        if (resource.storageId == null) throw new Error(`Font "${resource.filename}" has no uploaded file yet.`);
        const record = await getFontFile(resource.storageId);
        if (!record) throw new Error(`Font file for "${resource.filename}" is missing from storage.`);
        return record.data;
    }
    return resource.src;
};

const uploadResourceToDevice = async (resource, h3dArtifacts = null) => {
    if (resource.resourceType === 'synth') {
        const result = await deploySynthReferenceToPlate(resource.synthId, synthStore, props.settings.plate, uploadDeviceFile);
        if (!fileListRef.value.some((f) => f.filename === result.filename)) {
            fileListRef.value.push({ ...resource, filename: result.filename });
        }
        return result;
    }
    const dataUrl = await getUploadPayload(resource, h3dArtifacts);
    const base64data = resource.resourceType === 'model3d' ? dataUrl : dataUrlToBase64(dataUrl);
    await uploadDeviceFile(props.settings.plate, `L:/${resource.filename}`, base64data);
    if (!fileListRef.value.some((f) => f.filename === resource.filename)) {
        fileListRef.value.push(resource);
    }
};

// Uploads every resource currently used in the design (images + fonts) in
// one go, instead of dragging each one to the remote pane by hand.
const uploadAllUsedResources = async () => {
    if (isBulkUploading.value || designResources.value.length === 0) return;
    isBulkUploading.value = true;
    const items = designResources.value;
    bulkUploadProgress.value = { done: 0, total: items.length };
    const failed = [];
    let h3dArtifacts = null;
    try {
        if (items.some((resource) => resource.resourceType === 'model3d')) {
            h3dArtifacts = await buildUsedH3dResourceArtifacts(props.layer, model3dStore);
        }
    } catch (error) {
        isBulkUploading.value = false;
        uploadStatus.value = `3D resource compile failed: ${error.message || error}`;
        return;
    }
    for (const resource of items) {
        try {
            await uploadResourceToDevice(resource, h3dArtifacts);
        } catch (error) {
            console.error(`Failed to upload ${resource.filename}:`, error);
            failed.push(resource.filename);
        }
        bulkUploadProgress.value = { ...bulkUploadProgress.value, done: bulkUploadProgress.value.done + 1 };
    }
    isBulkUploading.value = false;
    uploadStatus.value = failed.length
        ? `Uploaded ${items.length - failed.length}/${items.length} used resources. Failed: ${failed.join(', ')}.`
        : `Uploaded all ${items.length} used resource${items.length === 1 ? '' : 's'} to remote.`;
};

const onDrop = async (target) => {
    if (!draggedFile.value || !draggedSource.value) return;

    if (draggedSource.value === 'local' && target === 'remote') {
        // Upload file to remote
        isLoadingRemote.value = true; // Start spinner for remote
        try {
            await uploadResourceToDevice(draggedFile.value);
            uploadStatus.value = `Uploaded ${draggedFile.value.filename} to remote.`;
        } catch (error) {
            console.error('Failed to upload file:', error);
            uploadStatus.value = `Failed to upload ${draggedFile.value.filename}: ${error.message || error}`;
        } finally {
            isLoadingRemote.value = false; // Stop spinner for remote
        }
    } else if (draggedSource.value === 'remote' && target === 'local') {
        // Check if the file already exists in the local files
        const isDuplicate = exportImages.value.some(
            (file) => file.filename === draggedFile.value.filename
        );

        if (isDuplicate) {
            uploadStatus.value = `File ${draggedFile.value.filename} already exists in local files.`;
            return; // Prevent the drop action
        }

        // Download file to local
        isLoadingLocal.value = true; // Start spinner for local
        try {
            console.log('Downloading file:', draggedFile.value.filename);
            const fileData = await downloadDeviceFile(
                props.settings.plate,
                `L:/${draggedFile.value.filename}`
            );

            console.log('File data:', fileData);

            // Convert byte array to Base64 string
            const byteArray = new Uint8Array(fileData.data);
            const base64String = `data:image/png;base64,${bytesToBase64(byteArray)}`;
            console.log('Base64 String:', base64String);

            // Store the image in the database
            await imageStore.addImage(base64String, fileData.filename);
            uploadStatus.value = `Downloaded ${draggedFile.value.filename} to local.`;
        } catch (error) {
            console.error('Failed to download file:', error);
            uploadStatus.value = `Failed to download ${draggedFile.value.filename}.`;
        } finally {
            isLoadingLocal.value = false; // Stop spinner for local
        }
    }

    // Reset drag state
    draggedFile.value = null;
    draggedSource.value = null;
};

const formatFileSize = (size) => {
    if (!size) return 'Unknown';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleDateString();
};

const toggleDeleteConfirmation = (index) => {
    // Toggle the confirmation balloon for the clicked file
    deleteConfirmationIndex.value = deleteConfirmationIndex.value === index ? null : index;
};

const confirmDelete = async (filename) => {
    // Proceed with the delete operation
    deleteConfirmationIndex.value = null; // Close the confirmation balloon

    isLoadingRemote.value = true;
    try {
        await deleteDeviceFile(props.settings.plate, `L:/${filename}`);

        // Remove the file from the remote file list
        fileListRef.value = fileListRef.value.filter((file) => file.filename !== filename);

        uploadStatus.value = `Deleted ${filename} from remote.`;
    } catch (error) {
        console.error('Failed to delete file:', error);
        uploadStatus.value = `Failed to delete ${filename}.`;
    } finally {
        isLoadingRemote.value = false; // Stop spinner for remote
    }
};

const cancelDelete = () => {
    // Close the confirmation balloon without deleting
    deleteConfirmationIndex.value = null;
};

const downloadToImageStore = async (filename) => {
    savingToStore.value.add(filename);
    console.log(`[HTTP Transfer] Starting download of "${filename}" to image store...`);
    
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            if (attempt > 1) {
                console.log(`[HTTP Transfer] Retry attempt ${attempt}/${maxRetries} for "${filename}"...`);
            }
            
            const fileData = await downloadDeviceFile(props.settings.plate, `L:/${filename}`);
            
            console.log('[HTTP Transfer] File data received:', {
                filename: fileData.filename || 'N/A',
                dataLength: fileData.data?.length || 0,
                dataType: typeof fileData.data,
                isArray: Array.isArray(fileData.data)
            });
            
            // Convert byte array to Base64 string
            const byteArray = new Uint8Array(fileData.data);
            console.log('[HTTP Transfer] Created Uint8Array with length:', byteArray.length);
            
            // Detect image type from filename
            const extension = filename.toLowerCase().split('.').pop();
            let mimeType = 'image/png'; // default
            if (extension === 'jpg' || extension === 'jpeg') {
                mimeType = 'image/jpeg';
            } else if (extension === 'gif') {
                mimeType = 'image/gif';
            } else if (extension === 'bmp') {
                mimeType = 'image/bmp';
            } else if (extension === 'webp') {
                mimeType = 'image/webp';
            }
            
            console.log('[HTTP Transfer] Detected MIME type:', mimeType);
            
            const base64String = `data:${mimeType};base64,${bytesToBase64(byteArray)}`;
            
            console.log('[HTTP Transfer] Base64 string created:', {
                length: base64String.length,
                prefix: base64String.substring(0, 50) + '...'
            });
            
            // Add to image store
            console.log('[HTTP Transfer] Calling imageStore.addImage...');
            const imageId = await imageStore.addImage(base64String, filename);
            console.log(`[HTTP Transfer] ✓ File "${filename}" added to image store successfully with ID:`, imageId);
            
            // Notify parent component that image store was updated
            emit('imageStoreUpdated');
            
            // Clear the saving indicator first
            savingToStore.value.delete(filename);
            savingToStore.value = new Set(savingToStore.value);
            
            // Show success indicator
            successfullySaved.value.add(filename);
            
            // Remove success indicator after 3 seconds
            setTimeout(() => {
                successfullySaved.value.delete(filename);
                successfullySaved.value = new Set(successfullySaved.value);
            }, 3000);
            
            uploadStatus.value = `Saved ${filename} to image store.`;
            
            // Success - break out of retry loop
            return;
            
        } catch (error) {
            lastError = error;
            console.error(`[HTTP Transfer] Attempt ${attempt}/${maxRetries} failed:`, error.message);
            
            // If it's the last attempt, throw the error
            if (attempt === maxRetries) {
                break;
            }
            
            // Wait a bit before retrying (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
    }
    
    // If we get here, all retries failed
    console.error("[HTTP Transfer] All retry attempts failed for file:", filename);
    console.error("[HTTP Transfer] Final error:", lastError);
    console.error("[HTTP Transfer] Error stack:", lastError?.stack);
    
    const errorMsg = lastError?.message || 'Unknown error';
    if (errorMsg.toLowerCase().includes('timeout')) {
        uploadStatus.value = `Connection timeout downloading "${filename}". Please try again.`;
    } else {
        uploadStatus.value = `Failed to save "${filename}" to image store: ${errorMsg}`;
    }
    
    savingToStore.value.delete(filename);
    savingToStore.value = new Set(savingToStore.value);
};
</script>

<style scoped>
/* Hide scrollbars for all browsers */
.no-scrollbar {
    scrollbar-width: none;
    /* Firefox */
    -ms-overflow-style: none;
    /* Internet Explorer 10+ */
}

.no-scrollbar::-webkit-scrollbar {
    display: none;
    /* Chrome, Safari, and Edge */
}
</style>
